import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Mail, Send, Users, Trash2, Loader2, CheckCircle2, AlertCircle, FileText, Clock } from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const NewsletterPanel = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0 });
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('compose'); // compose | subscribers | history

  // Compose form
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [testMode, setTestMode] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subsRes, campRes] = await Promise.all([
        axios.get(`${API}/newsletter/subscribers`, { withCredentials: true }),
        axios.get(`${API}/newsletter/campaigns`, { withCredentials: true }),
      ]);
      setSubscribers(subsRes.data.subscribers || []);
      setStats({ total: subsRes.data.total || 0, active: subsRes.data.active || 0 });
      setCampaigns(campRes.data.campaigns || []);
    } catch (err) {
      console.error('Error loading newsletter data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRemove = async (email) => {
    if (!window.confirm(`¿Dar de baja a ${email}?`)) return;
    try {
      await axios.delete(`${API}/newsletter/subscribers/${encodeURIComponent(email)}`, { withCredentials: true });
      await loadData();
    } catch (err) {
      alert('Error al dar de baja');
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !htmlContent.trim()) {
      alert('Asunto y contenido son requeridos');
      return;
    }
    const confirmMsg = testMode
      ? '¿Enviar correo de PRUEBA al admin?'
      : `¿Enviar newsletter a ${stats.active} suscriptor(es)? Esta acción no se puede deshacer.`;
    if (!window.confirm(confirmMsg)) return;

    setSending(true);
    setSendResult(null);
    try {
      const res = await axios.post(
        `${API}/newsletter/send`,
        { subject: subject.trim(), html_content: htmlContent, test_mode: testMode },
        { withCredentials: true }
      );
      setSendResult({ type: 'success', data: res.data });
      if (!testMode) {
        setSubject('');
        setHtmlContent('');
      }
      await loadData();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error al enviar';
      setSendResult({ type: 'error', message: typeof detail === 'string' ? detail : JSON.stringify(detail) });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6" data-testid="newsletter-panel">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-600" />
            Newsletter
          </h2>
          <p className="text-sm text-gray-500 mt-1">Gestiona suscriptores y envía campañas por email vía Resend.</p>
        </div>

        {/* Stats */}
        <div className="flex gap-3">
          <div className="px-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
            <div className="text-2xl font-bold text-gray-900" data-testid="stat-total">{stats.total}</div>
          </div>
          <div className="px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl shadow-sm">
            <div className="text-xs text-emerald-600 uppercase tracking-wide">Activos</div>
            <div className="text-2xl font-bold text-emerald-700" data-testid="stat-active">{stats.active}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        {[
          { id: 'compose', label: 'Redactar', icon: Send },
          { id: 'subscribers', label: 'Suscriptores', icon: Users },
          { id: 'history', label: 'Historial', icon: Clock },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              data-testid={`newsletter-tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeView === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Compose */}
      {activeView === 'compose' && (
        <form onSubmit={handleSend} className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4" data-testid="newsletter-compose-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Asunto</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ej: Novedades de JG Models — Semana 12"
              required
              data-testid="newsletter-subject-input"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Contenido HTML
              <span className="text-xs text-gray-500 font-normal ml-2">(usa CSS inline)</span>
            </label>
            <textarea
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              placeholder={`<h2 style="color:#fff;">¡Hola!</h2>\n<p>Esta semana tenemos...</p>\n<a href="https://jgmodels.com" style="color:#fbbf24;">Más info</a>`}
              required
              rows={12}
              data-testid="newsletter-html-input"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-1">Tu contenido será envuelto automáticamente en una plantilla con header/footer de marca.</p>
          </div>

          <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <input
              type="checkbox"
              id="test-mode"
              checked={testMode}
              onChange={(e) => setTestMode(e.target.checked)}
              data-testid="newsletter-testmode-checkbox"
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="test-mode" className="text-sm text-amber-900 cursor-pointer">
              <strong>Modo prueba</strong> — Solo enviar al admin (recomendado mientras el dominio no esté verificado en Resend)
            </label>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={sending}
              data-testid="newsletter-send-button"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50 transition-colors"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Enviando...' : testMode ? 'Enviar prueba' : `Enviar a ${stats.active} suscriptor(es)`}
            </button>
          </div>

          {sendResult && (
            <div
              data-testid="newsletter-send-result"
              className={`p-4 rounded-lg flex items-start gap-3 ${
                sendResult.type === 'success'
                  ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
              }`}
            >
              {sendResult.type === 'success' ? <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" /> : <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />}
              <div className="text-sm">
                {sendResult.type === 'success' ? (
                  <>
                    <strong>Enviado correctamente.</strong> Total: {sendResult.data.total} · Exitosos: {sendResult.data.sent} · Fallidos: {sendResult.data.failed}
                    {sendResult.data.failed > 0 && (
                      <ul className="mt-2 text-xs space-y-1">
                        {sendResult.data.failed_recipients.map((f, i) => (
                          <li key={i}>• {f.email}: {f.error}</li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <strong>{sendResult.message}</strong>
                )}
              </div>
            </div>
          )}
        </form>
      )}

      {/* Subscribers */}
      {activeView === 'subscribers' && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="newsletter-subscribers-list">
          {loading ? (
            <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : subscribers.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aún no hay suscriptores.</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Nombre</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Estado</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Suscrito</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subscribers.map((sub) => (
                  <tr key={sub.email} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{sub.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{sub.name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        sub.status === 'active'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {sub.status === 'active' ? 'Activo' : 'Dado de baja'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(sub.subscribed_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {sub.status === 'active' && (
                        <button
                          onClick={() => handleRemove(sub.email)}
                          data-testid={`newsletter-remove-${sub.email}`}
                          className="text-red-500 hover:text-red-700 transition-colors"
                          title="Dar de baja"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* History */}
      {activeView === 'history' && (
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden" data-testid="newsletter-history-list">
          {loading ? (
            <div className="p-8 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : campaigns.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Aún no se han enviado campañas.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {campaigns.map((c) => (
                <li key={c.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">{c.subject}</span>
                        {c.test_mode && <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">Prueba</span>}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(c.sent_at)} · por {c.sent_by_name}
                      </p>
                    </div>
                    <div className="text-right text-xs text-gray-600 flex-shrink-0">
                      <div><span className="text-emerald-600 font-medium">{c.sent_count}</span> enviados</div>
                      {c.failed_count > 0 && <div><span className="text-red-600 font-medium">{c.failed_count}</span> fallidos</div>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsletterPanel;
